import { createGlobalStyle } from 'styled-components';
import { fontFamily, fontSize, fontWeight, lineHeight } from './typography';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    font-family: ${fontFamily.primary};
    font-size: ${fontSize.base};
    font-weight: ${fontWeight.regular};
    line-height: ${lineHeight.normal};
    color: ${({ theme }) => theme.colors.text.primary};
    background-color: ${({ theme }) => theme.colors.background.default};
    overflow-x: hidden;
    transition: background-color 0.3s ease, color 0.3s ease;
    
    /* Subtle elegant background */
    background-image: 
      radial-gradient(circle at 20% 30%, ${({ theme }) => theme.colors.primary.main}05 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, ${({ theme }) => theme.colors.secondary.main}05 0%, transparent 50%);
    position: relative;
  }

  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${fontFamily.heading};
    font-weight: ${fontWeight.semibold};
    line-height: ${lineHeight.tight};
    margin-bottom: 1rem;
    letter-spacing: 0.01em;
  }

  h1 {
    font-size: ${fontSize['4xl']};
    font-weight: ${fontWeight.extrabold};
  }

  h2 {
    font-size: ${fontSize['3xl']};
    font-weight: ${fontWeight.bold};
  }

  h3 {
    font-size: ${fontSize['2xl']};
  }

  h4 {
    font-size: ${fontSize.xl};
  }

  h5 {
    font-size: ${fontSize.lg};
  }

  h6 {
    font-size: ${fontSize.base};
  }

  p {
    margin-bottom: 1rem;
  }

  a {
    color: ${({ theme }) => theme.colors.primary.main};
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: ${({ theme }) => theme.colors.primary.light};
    }
  }

  /* Lists */
  ul, ol {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
  }

  li {
    margin-bottom: 0.5rem;
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Buttons */
  button {
    font-family: inherit;
    font-size: inherit;
    cursor: pointer;
    border: none;
    background: none;
    outline: none;
  }

  /* Inputs */
  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    border-radius: 4px;
    padding: 0.75rem 1rem;
    background-color: ${({ theme }) => theme.colors.background.paper};
    color: ${({ theme }) => theme.colors.text.primary};
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary.main};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary.main}20;
    }

    &::placeholder {
      color: ${({ theme }) => theme.colors.text.tertiary};
    }
  }

  /* Scrollbar - Elegant theme */
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.paper};
    border-left: 1px solid ${({ theme }) => theme.colors.border.light};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.primary.main};
    border-radius: 5px;
    border: 2px solid ${({ theme }) => theme.colors.background.paper};

    &:hover {
      background: ${({ theme }) => theme.colors.primary.dark};
    }
  }

  /* Selection */
  ::selection {
    background-color: ${({ theme }) => theme.colors.primary.main}40;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  /* Focus visible for accessibility */
  :focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.main};
    outline-offset: 2px;
  }

  /* Skip to main content link for accessibility */
  .skip-to-content {
    position: absolute;
    top: -100%;
    left: 0;
    background: ${({ theme }) => theme.colors.primary.main};
    color: white;
    padding: 1rem 2rem;
    z-index: 9999;
    transition: top 0.3s ease;

    &:focus {
      top: 0;
    }
  }

  /* Utility classes */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1.5rem;
  }

  /* Responsive typography */
  @media (max-width: 768px) {
    h1 {
      font-size: ${fontSize['3xl']};
    }

    h2 {
      font-size: ${fontSize['2xl']};
    }

    h3 {
      font-size: ${fontSize.xl};
    }

    h4 {
      font-size: ${fontSize.lg};
    }
  }

  @media (max-width: 480px) {
    h1 {
      font-size: ${fontSize['2xl']};
    }

    h2 {
      font-size: ${fontSize.xl};
    }

    h3 {
      font-size: ${fontSize.lg};
    }
  }

  /* Print styles */
  @media print {
    body {
      background-color: white;
      color: black;
    }

    a {
      color: black;
      text-decoration: underline;
    }
  }

  /* Reduce motion for users who prefer it */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

export default GlobalStyle;